import { Static, Type } from '@sinclair/typebox';
import { parsePositiveSafeInteger, parsePrefixedPositiveId } from './utils';

/**
 * Champs personnalisés de fiche client — contrat partagé backend ↔ frontend.
 *
 * Une DÉFINITION appartient au pro (`pro_customer_custom_field`), une VALEUR à
 * un couple (client, définition) (`pro_customer_custom_field_value`). Le set de
 * types est volontairement IDENTIQUE à celui des champs personnalisés de
 * réservation (`BookingCustomFieldType`) : mêmes contrôles de saisie, même UI.
 * Les deux enums restent séparés (deux enums Postgres distincts) parce qu'ils
 * peuvent diverger — mais toute valeur ajoutée ici doit l'être AUSSI dans un
 * fichier de migration (mêmes valeurs, même ordre), sinon `verify-migrations`
 * casse la CI.
 */

// ── Enum ──────────────────────────────────────────────

export enum ProCustomerCustomFieldType {
  TEXT = 'TEXT',
  TEXTAREA = 'TEXTAREA',
  SELECT = 'SELECT',
  NUMBER = 'NUMBER',
  CHECKBOX = 'CHECKBOX',
  DATE = 'DATE',
}

export const ProCustomerCustomFieldTypeSchema = Type.Enum(ProCustomerCustomFieldType);

// ── Bornes (source de vérité unique, appliquées backend) ───────────────────

/** Définitions ACTIVES (non archivées) par pro. Même plafond que les champs de réservation. */
export const PRO_CUSTOMER_CUSTOM_FIELD_MAX_PER_PRO = 30;
/**
 * Définitions non supprimées par pro, archivées COMPRISES. Sans ce second
 * plafond, la boucle « créer → archiver → créer » accumulerait des lignes sans
 * borne (l'archivage libère une place active mais garde la ligne).
 */
export const PRO_CUSTOMER_CUSTOM_FIELD_MAX_TOTAL_PER_PRO = 100;
/** Longueur max d'une valeur stockée (colonne `Text`, borne applicative). */
export const PRO_CUSTOMER_CUSTOM_FIELD_VALUE_MAX_LENGTH = 2000;
/** Options d'une liste déroulante. */
export const PRO_CUSTOMER_CUSTOM_FIELD_MAX_OPTIONS = 50;

/**
 * Valeur stockée d'une case COCHÉE. Une case décochée n'est PAS stockée : elle
 * vaut « champ vide », exactement comme un champ jamais rempli — sinon les deux
 * états, identiques à l'écran, divergeraient à l'export, en campagne et en fusion.
 * `CHECKBOX_FALSE` n'est jamais stocké : il est reconnu à la saisie comme « décoché ».
 */
export const PRO_CUSTOMER_CUSTOM_FIELD_CHECKBOX_TRUE = 'true';
export const PRO_CUSTOMER_CUSTOM_FIELD_CHECKBOX_FALSE = 'false';

/**
 * Forme comparable d'un libellé : casse, accents, séparateurs et espaces
 * multiples neutralisés.
 *
 * Point de passage UNIQUE de la comparaison de libellés : unicité d'un champ
 * personnalisé (backend), détection d'un homonyme archivé depuis la fiche
 * client (frontend) et re-reconnaissance d'un en-tête de colonne à l'import
 * (détecteur) doivent répondre la même chose, sinon un libellé accepté ici
 * serait refusé là.
 */
export const normalizeCustomFieldLabel = (label: string): string =>
  label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\-./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// ── Saisie d'un champ NUMBER ──────────────────────────

/** Raison pour laquelle une saisie ne convient pas à un champ NUMBER. */
export enum ProCustomerCustomFieldNumberIssue {
  NOT_A_NUMBER = 'NOT_A_NUMBER',
  BELOW_MIN = 'BELOW_MIN',
  ABOVE_MAX = 'ABOVE_MAX',
}

/**
 * Bornes d'un champ NUMBER, inclusives. `null` (colonne en base) et absence
 * (contrat API) signifient toutes deux « sans borne ».
 */
export interface ProCustomerCustomFieldNumberBounds {
  min_value?: number | null;
  max_value?: number | null;
}

export type ProCustomerCustomFieldNumberCheck =
  | { valid: true; value: string }
  | { valid: false; issue: ProCustomerCustomFieldNumberIssue };

/** Notation décimale stricte, après nettoyage (virgule → point, espaces retirés). */
const CUSTOM_FIELD_DECIMAL_PATTERN = /^[+-]?\d+(?:\.\d+)?$/;

/**
 * Règle de saisie d'un champ NUMBER — point de passage UNIQUE : le serveur
 * (`normalizeCustomFieldValue`) et la fiche client (contrôle dès la saisie)
 * doivent répondre la même chose, sinon la fiche laisserait partir une valeur
 * que le serveur refuse, ou signalerait une valeur qu'il accepte.
 *
 * - Saisie vide (espaces compris) → `''`, valide : c'est l'ordre d'effacement.
 * - Notation décimale STRICTE, virgule (saisie FR, exports Excel FR) ou point,
 *   espaces ignorés (`1 000,5`). `Number()` accepterait aussi `1e3`, `0x10` ou
 *   `Infinity`, et `String(Number(x))` abîmerait les grands entiers.
 * - Bornes inclusives.
 * - Forme canonique : la saisie nettoyée, sans `+` ni zéros de tête — jamais sa
 *   re-sérialisation flottante.
 */
export const checkCustomFieldNumber = (
  raw: string,
  bounds: ProCustomerCustomFieldNumberBounds = {},
): ProCustomerCustomFieldNumberCheck => {
  const cleaned = raw.trim().replace(',', '.').replace(/\s/g, '');
  if (cleaned === '') return { valid: true, value: '' };
  if (!CUSTOM_FIELD_DECIMAL_PATTERN.test(cleaned)) {
    return { valid: false, issue: ProCustomerCustomFieldNumberIssue.NOT_A_NUMBER };
  }
  const parsed = Number(cleaned);
  if (typeof bounds.min_value === 'number' && parsed < bounds.min_value) {
    return { valid: false, issue: ProCustomerCustomFieldNumberIssue.BELOW_MIN };
  }
  if (typeof bounds.max_value === 'number' && parsed > bounds.max_value) {
    return { valid: false, issue: ProCustomerCustomFieldNumberIssue.ABOVE_MAX };
  }
  return { valid: true, value: cleaned.replace(/^\+/, '').replace(/^(-?)0+(?=\d)/, '$1') };
};

/**
 * Forme canonique stockée d'une saisie NUMBER, `''` pour un effacement, ou
 * `null` si elle ne convient pas — le contrat de `normalizeCustomFieldValue`.
 */
export const normalizeCustomFieldNumber = (
  raw: string,
  bounds: ProCustomerCustomFieldNumberBounds = {},
): string | null => {
  const check = checkCustomFieldNumber(raw, bounds);
  return check.valid ? check.value : null;
};

// ── Refus d'une valeur par le serveur ─────────────────

/**
 * Clé d'erreur métier (400) d'une écriture de fiche dont une valeur de champ
 * personnalisé ne convient pas à son type. Le corps porte `data.rejected`.
 */
export const API_ERROR_CUSTOMER_CUSTOM_FIELD_INVALID_VALUE = 'CUSTOMER_CUSTOM_FIELD_INVALID_VALUE';

/** Entrée refusée, telle que listée dans `data.rejected`. */
export interface ProCustomerCustomFieldRejectedValueObject {
  pro_customer_custom_field_id: number;
  /** Libellé du champ au moment du refus. */
  label: string;
  /** Valeur telle que reçue, avant normalisation. */
  value: string;
}

const isRejectedValueEntry = (entry: unknown): entry is ProCustomerCustomFieldRejectedValueObject => {
  if (entry === null || typeof entry !== 'object') return false;
  const record = entry as Record<string, unknown>;
  return typeof record['pro_customer_custom_field_id'] === 'number' && typeof record['value'] === 'string';
};

/**
 * Valeurs refusées par le serveur, indexées par id de champ, lues dans le corps
 * d'une erreur API. Carte vide pour toute autre erreur, ou un corps inattendu :
 * l'appelant n'a rien à signaler champ par champ.
 */
export const readRejectedCustomFieldValues = (errorBody: unknown): Map<number, string> => {
  const rejected = new Map<number, string>();
  if (errorBody === null || typeof errorBody !== 'object') return rejected;
  const body = errorBody as { error?: unknown; data?: unknown };
  if (body.error !== API_ERROR_CUSTOMER_CUSTOM_FIELD_INVALID_VALUE) return rejected;
  const entries =
    body.data !== null && typeof body.data === 'object' ? (body.data as { rejected?: unknown }).rejected : undefined;
  if (!Array.isArray(entries)) return rejected;
  for (const entry of entries) {
    if (isRejectedValueEntry(entry)) rejected.set(entry.pro_customer_custom_field_id, entry.value);
  }
  return rejected;
};

// ── Option (type SELECT) ──────────────────────────────

export const ProCustomerCustomFieldOptionSchema = Type.Object({
  label: Type.String({ maxLength: 200 }),
  value: Type.String({ maxLength: 200 }),
});
export type ProCustomerCustomFieldOptionObject = Static<typeof ProCustomerCustomFieldOptionSchema>;

// ── Définition (back-office pro) ──────────────────────

export const ProCustomerCustomFieldSchema = Type.Object({
  pro_customer_custom_field_id: Type.Number(),
  label: Type.String(),
  type: ProCustomerCustomFieldTypeSchema,
  placeholder: Type.Optional(Type.String()),
  options: Type.Optional(Type.Array(ProCustomerCustomFieldOptionSchema)),
  min_value: Type.Optional(Type.Integer()),
  max_value: Type.Optional(Type.Integer()),
  sort_order: Type.Number(),
  is_archived: Type.Boolean(),
});
export type ProCustomerCustomFieldObject = Static<typeof ProCustomerCustomFieldSchema>;

export const ListProCustomerCustomFieldResponseSchema = Type.Object({
  items: Type.Array(ProCustomerCustomFieldSchema),
});
export type ListProCustomerCustomFieldResponseObject = Static<typeof ListProCustomerCustomFieldResponseSchema>;

// ── CRUD (back-office pro) ────────────────────────────

export const CreateProCustomerCustomFieldRequestSchema = Type.Object({
  label: Type.String({
    minLength: 1,
    maxLength: 255,
    title: 'PRO_CUSTOMER_CUSTOM_FIELD_LABEL',
    placeholder: 'PRO_CUSTOMER_CUSTOM_FIELD_LABEL_PLACEHOLDER',
  }),
  type: ProCustomerCustomFieldTypeSchema,
  placeholder: Type.Optional(
    Type.String({
      maxLength: 255,
      title: 'PRO_CUSTOMER_CUSTOM_FIELD_PLACEHOLDER',
    }),
  ),
  options: Type.Optional(
    Type.Array(ProCustomerCustomFieldOptionSchema, { maxItems: PRO_CUSTOMER_CUSTOM_FIELD_MAX_OPTIONS }),
  ),
  // Entiers : colonnes `Int` en base. Une valeur fractionnaire (borne ou
  // position) doit être refusée en 400 par la validation plutôt que de faire
  // échouer Prisma en 500.
  min_value: Type.Optional(Type.Integer()),
  max_value: Type.Optional(Type.Integer()),
  sort_order: Type.Optional(Type.Integer()),
});
export type CreateProCustomerCustomFieldRequestObject = Static<typeof CreateProCustomerCustomFieldRequestSchema>;

/**
 * Borne d'une mise à jour : absente = inchangée, `null` = EFFACÉE. Sans ce
 * `null` explicite, une borne posée une fois ne pourrait plus jamais être
 * retirée : le pipe de validation supprime le `null` des propriétés optionnelles,
 * sauf celles qui l'admettent ET le demandent via `meta.keep_null`.
 */
const ClearableBoundSchema = Type.Optional(Type.Union([Type.Integer(), Type.Null()], { meta: { keep_null: true } }));

export const UpdateProCustomerCustomFieldRequestSchema = Type.Object({
  ...Type.Partial(CreateProCustomerCustomFieldRequestSchema).properties,
  min_value: ClearableBoundSchema,
  max_value: ClearableBoundSchema,
});
export type UpdateProCustomerCustomFieldRequestObject = Static<typeof UpdateProCustomerCustomFieldRequestSchema>;

export const ReorderProCustomerCustomFieldsRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Integer(), { minItems: 1 }),
});
export type ReorderProCustomerCustomFieldsRequestObject = Static<typeof ReorderProCustomerCustomFieldsRequestSchema>;

// ── Valeur portée par une fiche client ────────────────

/**
 * Valeur telle qu'ÉCRITE (création / mise à jour d'une fiche, fusion, import).
 * Une valeur vide (`''`) EFFACE le champ : c'est le seul moyen de vider un champ
 * sans supprimer sa définition.
 */
export const ProCustomerCustomFieldValueInputSchema = Type.Object({
  pro_customer_custom_field_id: Type.Number(),
  value: Type.String({ maxLength: PRO_CUSTOMER_CUSTOM_FIELD_VALUE_MAX_LENGTH }),
});
export type ProCustomerCustomFieldValueInputObject = Static<typeof ProCustomerCustomFieldValueInputSchema>;

/**
 * Valeur telle que RENVOYÉE sur une fiche client. Porte le libellé et le type
 * pour que l'UI affiche la réponse sans second aller-retour, et
 * `value_formatted` déjà prêt à l'affichage (case à cocher « Oui / Non », date
 * au format du pro — règle Dates : jamais de formatage côté frontend).
 */
export const ProCustomerCustomFieldValueSchema = Type.Object({
  pro_customer_custom_field_id: Type.Number(),
  label: Type.String(),
  type: ProCustomerCustomFieldTypeSchema,
  value: Type.String(),
  value_formatted: Type.String(),
});
export type ProCustomerCustomFieldValueObject = Static<typeof ProCustomerCustomFieldValueSchema>;

/**
 * Tableau de valeurs porté par `ProCustomerResponseSchema`.
 *
 * `meta.skip_csv` : l'export clients (`buildCsvFromSchema`) suit l'ordre des
 * clés du schéma et aplatirait ce tableau en une cellule JSON illisible et
 * non ré-importable. L'export émet À LA PLACE une colonne par champ actif,
 * intitulée du libellé du champ — ce que le détecteur de mapping sait
 * re-reconnaître à l'import (aller-retour export → édition → ré-import).
 */
export const ProCustomerCustomFieldValueListSchema = Type.Array(ProCustomerCustomFieldValueSchema, {
  meta: { skip_csv: true },
});

// ── Personnalisation des campagnes email ──────────────

/**
 * Préfixe des variables de campagne portant un champ personnalisé :
 * `{{customer:custom:<id>}}`. Les variables historiques
 * (`customer:first_name`, `customer:last_name`) gardent leur forme.
 */
export const CUSTOMER_CUSTOM_BINDING_PREFIX = 'customer:custom:';

export const buildCustomerCustomBindingKey = (proCustomerCustomFieldId: number): string =>
  `${CUSTOMER_CUSTOM_BINDING_PREFIX}${proCustomerCustomFieldId}`;

/** Renvoie l'id de champ porté par une clé de binding, ou `null` si ce n'en est pas une. */
export const parseCustomerCustomBindingKey = (key: string): number | null =>
  parsePrefixedPositiveId(key, CUSTOMER_CUSTOM_BINDING_PREFIX);

/**
 * Nom de la propriété de contact Mailjet portant un champ personnalisé.
 * Mailjet n'accepte que `[a-z0-9_]` : l'id numérique garantit un nom stable et
 * valide, indépendant du libellé (que le pro peut renommer à tout moment).
 */
export const buildMailjetPropertyForCustomField = (proCustomerCustomFieldId: number): string =>
  `custom_${proCustomerCustomFieldId}`;

/** Toutes les clés `customer:custom:<id>` présentes dans un contenu HTML / texte. */
export const extractCustomerCustomFieldIds = (content: string): number[] => {
  const ids = new Set<number>();
  const pattern = /\{\{customer:custom:(\d+)\}\}/g;
  let match: RegExpExecArray | null = pattern.exec(content);
  while (match !== null) {
    const parsed = parsePositiveSafeInteger(match[1]);
    if (parsed !== null) ids.add(parsed);
    match = pattern.exec(content);
  }
  return [...ids];
};
